import Quill, { RangeStatic } from 'quill';
// @ts-ignore
import Delta from 'quill-delta';
import 'quill-paste-smart';
import MagicUrl from 'quill-magic-url';
// @ts-ignore
import QuillEmoji from 'quill-emoji';
import 'quill-emoji/dist/quill-emoji.css';
import { BaseRenderer } from '@/modules/core/ui/renderers/BaseRenderer';
import { MentionBlot } from '@/modules/core/form/renderers/blots/MentionBlot';
import Tribute, { TributeItem, TributeOptions } from 'tributejs';
import { User } from '@/modules/user/models/User';
import { TributeConfig } from '@/modules/core/form/models/TributeConfig';
import ComponentService from '@/modules/core/app/services/ComponentService';
import { KeyCode } from '@/modules/core/ui/ui.constants';

export class RichTextEditorRenderer extends BaseRenderer {
  quill?: Quill;
  tribute?: Tribute<User>;
  editorId?: string;
  private lastMentionTriggerIndex: number | null = null;

  constructor(editorId: string) {
    super();

    this.editorId = editorId;
    Quill.register(MentionBlot, true);
    Quill.register('modules/magicUrl', MagicUrl, true);
    Quill.register('modules/emoji', QuillEmoji, true);
  }

  addQuill(
    onInput: () => string,
    initialText: string,
    placeholder: string,
    showEmoji: boolean,
    showLink: boolean,
    showToolbar: boolean
  ): void {
    const emojiHandlerFn: OmitThisParameter<(e: KeyboardEvent) => void> =
      this.emojiEscapeKeyHandler.bind(this);

    const toolbar:
      | boolean
      | {
          container: (string | { list: string })[][];
          handlers: { separator: () => void; emoji: () => void };
        } = showToolbar
      ? {
          container: this.getToolbarContainer(showLink, showEmoji),
          handlers: {
            separator: (): void => undefined,
            emoji: (): void => undefined
          }
        }
      : false;

    this.quill = new Quill(`#${this.editorId}`, {
      modules: {
        syntax: false,
        toolbar,
        keyboard: this.getKeyboardModule(),
        magicUrl: true,
        clipboard: {
          matchers: [
            [
              'span.mention',
              (node: HTMLElement): Delta => new Delta().insert({ mention: node.innerText })
            ]
          ]
        },
        'emoji-toolbar': true,
        'emoji-shortname': {
          onOpen: (): void => {
            // @ts-ignore
            this.quill.container.addEventListener('keydown', emojiHandlerFn);
          },
          onClose: (): void => {
            // @ts-ignore
            this.quill.container.removeEventListener('keydown', emojiHandlerFn);
          }
        }
      },
      theme: 'snow',
      placeholder
    });

    this.quill.on('text-change', onInput);
    this.processURLs();

    if (!initialText) {
      return;
    }

    this.quill.clipboard.dangerouslyPasteHTML(initialText, Quill.sources.USER);
    this.setSelection(this.quill.getLength());
  }

  addTribute(tributeConfig: TributeConfig): void {
    this.tribute = new Tribute(this.getTributeOptions(tributeConfig));
    this.tribute.attach(tributeConfig.element);

    tributeConfig.element.addEventListener('tribute-replaced', tributeConfig.onMention);
    tributeConfig.element.addEventListener('tribute-active-true', (): void => {
      this.lastMentionTriggerIndex = this.quill?.getSelection()?.index ?? null;
      tributeConfig.onShowMentions?.();
    });
    tributeConfig.element.addEventListener('tribute-active-false', (): void => {
      this.lastMentionTriggerIndex = null;
      tributeConfig.onHideMentions?.();
    });
  }

  getText(): string | undefined {
    if (!this.quill) {
      return;
    }

    const content: string = this.quill.root.innerHTML;
    const parsedContent: Document = new DOMParser().parseFromString(content, 'text/html');

    // Remove empty 'p' and 'li' elements at the end
    let elements: NodeListOf<Element> = parsedContent.querySelectorAll('p, li');
    for (let i: number = elements.length - 1; i >= 0; i--) {
      const el: Element = elements[i];
      if (el.textContent?.trim() === '' && !el.querySelector('img, video, audio')) {
        el.remove();
        // Update the elements NodeList to check the updated content
        elements = parsedContent.querySelectorAll('p, li');

        continue;
      }
      // Stop checking once a non-empty element is found
      break;
    }

    if (
      this.quill.getText().trim().length === 0 &&
      !parsedContent.body.innerHTML.includes('emojiblot') &&
      !parsedContent.body.innerHTML.includes('mention')
    ) {
      return '';
    }

    return parsedContent.body.innerHTML;
  }

  insertMention(text: string, triggerLength?: number, hasMentionText?: boolean): void {
    if (!this.quill) {
      return;
    }

    let index: number | undefined;

    if (triggerLength != null && triggerLength >= 0 && this.lastMentionTriggerIndex != null) {
      index = Math.max(0, this.lastMentionTriggerIndex - triggerLength - (hasMentionText ? 1 : 0));
    } else {
      index = this.quill.getSelection()?.index ?? this.quill.getLength();
    }

    this.quill.insertEmbed(index, 'mention', text);
    this.setSelection(index + 1);
  }

  isCreatingList(): boolean {
    const range: RangeStatic | null | undefined = this.quill?.getSelection();

    if (!range) {
      return false;
    }

    return !!this.quill?.getFormat(range).list;
  }

  getCursorFromBottom(): string | undefined {
    const range: RangeStatic | null | undefined = this.quill?.getSelection();
    if (!range || !this.quill) {
      return undefined;
    }

    const lineCount: number = this.quill.getText(0, range.index).split('\n').length - 1;

    return `${35 + (this.getTotalLines() - lineCount - 2) * 21}px`;
  }

  getToolbarFromBottom(): string {
    return `${60 + (this.getTotalLines() - 1) * 21}px`;
  }

  toggleDisabled(disabled: boolean = false): void {
    if (!this.quill) {
      return;
    }

    this.quill.enable(!disabled);
    if (!disabled) {
      this.quill.focus();
    }
  }

  private setSelection(index: number, length: number = 0): void {
    this.quill?.setSelection(index, length);
  }

  private getTotalLines(): number {
    return this.quill ? this.quill.getText().split('\n').length : 1;
  }

  private getToolbarContainer(
    showLink: boolean,
    showEmoji: boolean
  ): (string | { list: string })[][] {
    const items: (string | { list: string })[] = [
      'bold',
      'italic',
      'underline',
      'separator',
      { list: 'ordered' },
      { list: 'bullet' }
    ];

    if (showLink) {
      items.splice(3, 0, 'link');
    }

    if (showEmoji) {
      items.push('separator');
      items.push('emoji');
    }

    return [items];
  }

  private getKeyboardModule(): Record<string, unknown> {
    return {
      bindings: {
        tributeSelectOnEnter: {
          key: 13,
          shortKey: false,
          handler: (): boolean => !this.tribute?.isActive
        }
      }
    };
  }

  private getTributeOptions(tributeConfig: TributeConfig): TributeOptions<User> {
    return {
      trigger: tributeConfig.mentionables.length ? tributeConfig.trigger : `${Math.random()}`,
      values: tributeConfig.mentionables,
      lookup: tributeConfig.mentionableLookupField,
      fillAttr: tributeConfig.mentionableLookupField,
      selectTemplate: (): string => '',
      menuItemTemplate: (item: TributeItem<User>): string =>
        ComponentService.renderComponentToHtml(tributeConfig.mentionOptionComponent, {
          // @ts-ignore
          mentionable: item.original,
          // @ts-ignore
          selectionText: item.string
        }),
      noMatchTemplate: (): string => '<span class="is-hidden" />',
      menuShowMinLength: tributeConfig.triggerLength,
      positionMenu: true
    };
  }

  private processURLs(): void {
    if (!this.quill) {
      return;
    }

    new MutationObserver((mutations: MutationRecord[]): void => {
      mutations.forEach((mutation: MutationRecord): void => {
        if (mutation.type !== 'childList') {
          return;
        }

        mutation.addedNodes.forEach((node: Node): void => {
          if (node.nodeName !== 'A') {
            return;
          }

          const href: string | null = (node as Element).getAttribute('href');

          if (!href || href.startsWith('http')) {
            (node as Element).setAttribute('target', '_blank');
            return;
          }

          (node as Element).setAttribute('href', `//${href}`);
          (node as Element).setAttribute('target', '_blank');
        });
      });
    }).observe(this.quill.root, { attributes: true, childList: true, subtree: true });
  }

  private emojiEscapeKeyHandler(e: KeyboardEvent): void {
    if (e.key.toLowerCase() !== KeyCode.ESCAPE.name) {
      return;
    }

    const emojiSelector: NodeListOf<HTMLElement> = document.querySelectorAll(
      '.emoji_completions, #emoji-palette'
    );

    emojiSelector.forEach((completion: HTMLElement) => {
      completion.style.display = 'none';
    });

    if (!this.quill || this.quill?.hasFocus()) {
      return;
    }

    this.quill.focus();
  }
}
